import { FC } from "react";
import { Box, Typography } from "@mui/material";
import { IconPerson } from "@/assets/icons";
import { Avatar } from "@/components/elements/Avatar";
import { dayjs } from "@/utils/dayjs";
import { DATE_FORMAT } from "@/constants/datetime";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { text, background, divider } from "@/theme/colors";
import { useSharedFileSignedUrl } from "@/api/services/main/sharing";

type TSharedTalentProfileHeaderProps = {
  defaultValues: Partial<TTalentForm>;
  /** When true, show default avatar placeholder; when false, show real avatar */
  isMasked?: boolean;
  /** Share URL token for fetching signed URLs without auth */
  shareUrl?: string;
};

/** Profile header for shared talent page — handles both full and masked display */
export const SharedTalentProfileHeader: FC<TSharedTalentProfileHeaderProps> = ({ defaultValues, isMasked = true, shareUrl }) => {
  /** Fetch avatar signed URL via sharing API (no auth needed) */
  const { data: avatarSignedUrl } = useSharedFileSignedUrl(
    !isMasked ? defaultValues.avatarFilePath : undefined,
    shareUrl,
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
      {isMasked || !defaultValues.avatarFilePath ? (
        /* Masked or no avatar: default placeholder */
        <Box
          sx={{
            width: 100,
            height: 133,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: background.white,
            border: `1px dashed ${divider.middle}`,
            borderRadius: "2px",
          }}
        >
          <IconPerson fontSize={100} color="#CACDCD" width={80} height={80} />
        </Box>
      ) : (
        /* Full: real avatar using shared signed URL */
        <Avatar
          filePath={undefined}
          imageSrc={avatarSignedUrl?.data.signedUrl}
          width={100}
          height={133}
        />
      )}

      <Box ml={3} sx={{ flex: 1 }}>
        <Typography
          variant="head20Semi"
          sx={{
            mb: 1,
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          {defaultValues.fullName || ""}
        </Typography>

        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body14" color={text.secondary} sx={{ display: "flex", alignItems: "center" }}>
            登録日:
            <span style={{ marginLeft: 4 }}>{defaultValues.createdAt ? dayjs(defaultValues.createdAt).format(DATE_FORMAT) : ""}</span>
          </Typography>
          <Typography variant="body14" color={text.secondary}>
            |
          </Typography>
          <Typography variant="body14" color={text.secondary} sx={{ display: "flex", alignItems: "center" }}>
            更新日:
            <span style={{ marginLeft: 4 }}>{defaultValues.updatedAt ? dayjs(defaultValues.updatedAt).format(DATE_FORMAT) : ""}</span>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
